import type { ReplyWindow } from '@chatapp-inbox/shared';

export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;

/**
 * Teto do `setTimeout`: o atraso é um inteiro de 32 bits com sinal (~24,8 dias). Acima disso o
 * navegador o dispara na hora (o prazo de uma janela de resposta nunca chega perto, mas o servidor
 * não impõe limite às horas da regra, e um prazo longo não pode virar um laço de disparos imediatos).
 */
export const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** Com tão pouco tempo (ou menos) o compositor mostra a contagem regressiva. */
export const COUNTDOWN_FROM_MS = 2 * HOUR_MS;

/** Faltando isto ou menos, a contagem ganha destaque (peso do texto, além da cor) e um segundo anúncio. */
export const URGENT_FROM_MS = 10 * MINUTE_MS;

/**
 * Situação da janela de resposta de uma conversa num instante:
 * - `none`: o provedor não tem regra (`replyWindow` nulo), sempre dá para responder;
 * - `open`: há regra e o prazo ainda não chegou;
 * - `closed`: há regra e o prazo passou, ou o cliente nunca escreveu (`expiresAt` nulo).
 */
export type WindowStatus =
  | { kind: 'none' }
  | { kind: 'open'; expiresAt: number; remainingMs: number; now: number }
  | { kind: 'closed'; expiresAt: number | null };

/** O prazo em ms, ou `null` se não há (cliente nunca escreveu, sem regra ou texto ilegível). */
export function deadlineOf(replyWindow: ReplyWindow | null | undefined): number | null {
  if (!replyWindow || replyWindow.expiresAt === null) return null;
  const expiresAt = Date.parse(replyWindow.expiresAt);
  return Number.isNaN(expiresAt) ? null : expiresAt;
}

/**
 * Aberta sse `now < expiresAt` (no instante exato do prazo já está fechada). `expiresAt` nulo ou
 * ilegível conta como fechada: o servidor é quem decide, e fechada só custa uma explicação, enquanto
 * "aberta" por engano custaria um envio recusado.
 */
export function windowStatus(
  replyWindow: ReplyWindow | null | undefined,
  now: number,
): WindowStatus {
  // `undefined` (conversa montada à mão, sem o campo) vale o mesmo que `null`: sem regra.
  if (!replyWindow) return { kind: 'none' };
  const expiresAt = deadlineOf(replyWindow);
  if (expiresAt === null) return { kind: 'closed', expiresAt: null };
  if (now < expiresAt) return { kind: 'open', expiresAt, remainingMs: expiresAt - now, now };
  return { kind: 'closed', expiresAt };
}

/** Em que ponto da contagem a janela aberta está (decide o destaque e os anúncios). */
export type CountdownPhase = 'far' | 'soon' | 'urgent';

export function countdownPhase(remainingMs: number): CountdownPhase {
  if (remainingMs <= URGENT_FROM_MS) return 'urgent';
  if (remainingMs <= COUNTDOWN_FROM_MS) return 'soon';
  return 'far';
}

/**
 * Tempo que falta, em linguagem curta: "25 min", "3 h", "1 h 20 min". Arredonda os minutos PARA
 * CIMA (com 20 s restantes ainda se pode responder: "1 min", nunca "0 min") e não mostra "0 min"
 * nas horas cheias.
 */
export function formatRemaining(remainingMs: number): string {
  const total = Math.max(1, Math.ceil(remainingMs / MINUTE_MS));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}
